import { Link } from "react-router-dom";
import {
    FileText,
    ClipboardList,
    FolderOpen,
    MessageCircle,
    Wallet,
    Headphones,
    Target,
    ArrowUpRight,
} from "lucide-react";

const FOCUS = [
    { icon: FileText, title: "Online Form Filling" },
    { icon: ClipboardList, title: "Registration Services" },
    { icon: FolderOpen, title: "Digital Documentation" },
    { icon: MessageCircle, title: "WhatsApp Communication" },
    { icon: Wallet, title: "Money Transfer Distribution" },
    { icon: Headphones, title: "Customer Support" },
];

export default function AboutPage() {
    return (
        <section
            className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
            data-testid="about-page"
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                <div>
                    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass-card text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                        About Us
                    </span>
                    <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                        A modern firm for
                        <br />
                        <span className="text-gradient-brand">everyday digital needs.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        <span className="font-semibold text-foreground">Smart Solutions</span> is a sole
                        proprietorship firm providing various online services to individuals and
                        businesses. We focus on making digital processes simple, secure and affordable.
                    </p>

                    <div className="mt-8 glass-card rounded-3xl p-6 lg:p-7">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl btn-gradient flex items-center justify-center text-white">
                                <Target className="h-5 w-5" />
                            </div>
                            <h2 className="text-lg sm:text-xl font-display font-semibold tracking-tight">
                                Our Mission
                            </h2>
                        </div>
                        <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                            Deliver fast, reliable and affordable online services through modern
                            technology — while keeping human support just a call away.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="about-cta-contact"
                            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-full btn-gradient ripple text-white text-sm font-semibold"
                        >
                            Work with us <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>

                <div>
                    <h3 className="text-xs uppercase tracking-[0.24em] font-semibold text-muted-foreground">
                        What we focus on
                    </h3>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {FOCUS.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div
                                    key={f.title}
                                    data-testid={`about-focus-${i}`}
                                    className="glass-card rounded-2xl p-4 flex items-center gap-3 hover-lift fade-in-up"
                                    style={{ animationDelay: `${80 * i}ms` }}
                                >
                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#3BC7FF] to-[#7B61FF] p-[1.5px]">
                                        <div className="h-full w-full rounded-[10px] bg-background/80 backdrop-blur flex items-center justify-center">
                                            <Icon className="h-4 w-4" strokeWidth={1.8} />
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium">{f.title}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                        <div className="glass-card rounded-2xl p-4 text-center">
                            <div className="font-display font-bold text-2xl">6+</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                                Services
                            </div>
                        </div>
                        <div className="glass-card rounded-2xl p-4 text-center">
                            <div className="font-display font-bold text-2xl">10k+</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                                Clients
                            </div>
                        </div>
                        <div className="glass-card rounded-2xl p-4 text-center">
                            <div className="font-display font-bold text-2xl">99.9%</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                                Uptime
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
